import { Controller } from "@hotwired/stimulus";

/** 
 * Proxy used to communicate with a controller that exists in a different scope. 
 * Convention: The proxy identifier must be {subject}-proxy, where {subject} is the name of the controller that the proxy is communicating with.
 * The proxy will lookup the subject using the {subject} identifier and the id from the data-{subject}-proxy-id-value on the element.
 * 
 * Some other approaches to this problem include
 * 1. Dispatching the event on the document object and listening for it in the other controller. The event needs to include a subjectId, in case multiple controllers with the same identifier exist on the page. The subject must listen for the event on the document and check the subjectId against its own id. Works but cumbersome. 
 * 2. Putting an outlet directly on the controller that needs access to the subject. This is the simplest solution but causes code coupling and circular dependencies that can be hard to manage in javascript. For instance, if the dialog module is tied to the report module, it might be difficult to compile them separately for cases where only one module is needed on a page.
 * 3. Putting an outlet on the proxy controller. Works, but cannot be done in an abstract way because the name of the outlet must match the name of the subject controller. Otherwise Stimulus doesn't know what controller to look for on the element, since there can be multiple. See https://github.com/hotwired/stimulus/issues/641 for more information.
 **/
export class ProxyController extends Controller {

    static values = {id: String};
    declare static proxies: string[];

    declare readonly idValue: string;

    initialize(): void {
        const subclass = Object.getPrototypeOf(this).constructor as typeof ProxyController;
        subclass.proxies.forEach((method) => {
            this[method] = (e: Event) => this.subject[method](e);
        });
    }

    /** Purposely not memoized, to mimic dynamic behavior of Stimulus JS mutation observer. */
    get subject(): Controller {
        let element = document.getElementById(this.idValue);
        let identifier = this.identifier.replace('-proxy', '');
        return this.application.getControllerForElementAndIdentifier(element, identifier);
    }
}
